import React, { useRef, useCallback, useMemo, useEffect, forwardRef } from 'react';
import { connect } from 'react-redux';
import { useDrag, useDrop } from 'react-dnd';

import { makeGetTask, makeGetFilteredTask } from 'selectors/tasks';

import { isTempId } from 'utils/helpers';

import { useCanUser } from 'shared-hooks/permission';

import Task from 'components/task';

const DND_ITEM_TYPE = 'task';

const TaskDNDDraggable = forwardRef(({ moveTask, id, selected, index, taskGroupId, milestoneId, publicPage, current, order, simpleTask, draggedTask, showProject, labelsFetched, placeholder, name, completed, archived, removed, draggable, isTemp, isStatic, taskGroupTaskProps, style, hiddenKanbanFields, onClick, onHeightChange, onDragStart, onDragEnd, onSelect, onMeasureChange }, ref) => {
    const dndRef = useRef(null);
    const canMoveTask = useCanUser('moveTask');
    const canCompleteTask = useCanUser('completeTask');

    const handleMove = useCallback((item, monitor, isDrop) => {
        if (!dndRef.current && !isDrop) {
            return;
        }

        if (completed && !canCompleteTask) {
            return;
        }

        const dragIndex = item.index;
        const hoverIndex = index;
        const dragTaskGroupId = item.taskGroupId;
        const dragMilestoneId = item.milestoneId;
        let beforeMiddle = false;

        if (!isDrop) {
            if (dragTaskGroupId === taskGroupId && dragMilestoneId === milestoneId && dragIndex === hoverIndex) {
                return;
            }

            const hoverBoundingRect = dndRef.current.getBoundingClientRect();
            const hoverMiddleY = hoverBoundingRect.height / 2;
            const clientOffset = monitor.getClientOffset();
            const hoverClientY = clientOffset.y - hoverBoundingRect.top;

            beforeMiddle = hoverClientY < hoverMiddleY;

            // Dragging downwards
            if (dragTaskGroupId === taskGroupId && dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
                return;
            }

            // Dragging upwards
            if (dragTaskGroupId === taskGroupId && dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
                return;
            }
        }

        const indexCorrection = item.taskGroupId !== taskGroupId ? (beforeMiddle ? 0 : 1) : 0;

        const source = {
            id: item.id,
            taskGroupId: item.taskGroupId,
            milestoneId: item.milestoneId,
            completed: item.completed,
            archived: item.archived,
            taskGroupTaskProps: item.taskGroupTaskProps,
            order: item.order,
            index: item.index
        };

        const destination = {
            id,
            taskGroupId,
            milestoneId,
            completed,
            archived,
            order,
            taskGroupTaskProps,
            isBefore: beforeMiddle,
            index: index + indexCorrection
        };

        moveTask({
            source,
            destination,
            save: Boolean(isDrop)
        });

        item.index = hoverIndex + indexCorrection;
        item.taskGroupId = taskGroupId;
        item.milestoneId = milestoneId;
        item.order = order;
        item.archived = archived;
        item.completed = completed;
        item.taskGroupTaskProps = taskGroupTaskProps;
    }, [id, index, taskGroupId, milestoneId, completed, archived, order, taskGroupTaskProps, canCompleteTask, moveTask]);

    const dropSpec = useMemo(() => ({
        accept: DND_ITEM_TYPE,
        collect: monitor => ({
            isOver: !!monitor.isOver(),
        }),
        hover(item, monitor, component) {
            if (!dndRef.current) {
                return;
            }

            handleMove(item, monitor);
        },
        drop(item, monitor) {
            onDragEnd && onDragEnd({
                id,
                taskGroupId,
                milestoneId
            });

            handleMove(item, monitor, true);
        }
    }), [id, taskGroupId, milestoneId, dndRef, handleMove, onDragEnd]);

    const dragSpec = useMemo(() => ({
        type: DND_ITEM_TYPE,
        previewOptions: {
            captureDraggingState: true
        },
        collect: monitor => ({
            isDragging: monitor.isDragging(),
        }),
        item(monitor) {
            onDragStart && onDragStart({
                id,
                taskGroupId,
                milestoneId,
                completed,
                archived,
                order,
                taskGroupTaskProps
            });

            return { id, index, taskGroupId, milestoneId, completed, archived, order };
        },
        end(item, monitor) {
            if (monitor.didDrop()) {
                return false;
            }

            onDragEnd && onDragEnd();
            handleMove(item, monitor, true);
        },
        canDrag(monitor) {
            return !placeholder && draggable && !isStatic;
        }
    }), [id, index, taskGroupId, milestoneId, placeholder, draggable, isStatic, completed, archived, order, taskGroupTaskProps, handleMove, onDragStart, onDragEnd]);

    const [{ isOver }, drop] = useDrop(dropSpec);
    const [{ isDragging }, drag, preview] = useDrag(dragSpec);

    const placeholderRef = useCallback(el => {
        drop(el);
        dndRef.current = el;
        ref?.(el);
    }, [drop, ref]);

    const refWithDnd = useCallback(el => {
        preview(el);

        if (!completed || canCompleteTask) {
            drop(el);
        }

        drag(el);
        dndRef.current = el;
        ref?.(el);
    }, [preview, drop, drag, ref, completed, canCompleteTask]);

    if (!id) {
        return (
            <div ref={placeholderRef} className={'task' + (placeholder ? ' fake-dropzone' : '')}>
                <div className="task-inner">
                    <div className="task-name-alt">{name}</div>
                </div>
            </div>
        );
    }

    if (removed) {
        return null;
    }

    if (canMoveTask && draggable && !isTemp) {
        return (
            <Task
                ref={refWithDnd}
                id={id}
                index={index}
                selected={selected}
                publicPage={publicPage}
                current={current}
                simpleTask={simpleTask}
                draggedTask={draggedTask}
                showProject={showProject}
                labelsFetched={labelsFetched}
                isDragging={isDragging}
                isOver={isOver}
                hiddenKanbanFields={hiddenKanbanFields}
                style={style}
                onClick={onClick}
                onHeightChange={onHeightChange}
                onSelect={onSelect}
                onMeasureChange={onMeasureChange}
            />
        );
    }

    return <Task 
        ref={ref}
        id={id}
        index={index}
        selected={selected}
        publicPage={publicPage}
        current={current}
        simpleTask={simpleTask}
        draggedTask={draggedTask}
        showProject={showProject}
        labelsFetched={labelsFetched}
        isDragging={isDragging}
        isOver={isOver}
        hiddenKanbanFields={hiddenKanbanFields}
        style={style}
        onClick={onClick}
        onHeightChange={onHeightChange}
        onSelect={onSelect}
        onMeasureChange={onMeasureChange}
    />
});

TaskDNDDraggable.defaultProps = {
    draggable: true
}

const makeMapStateToProps = () => {
    const getTask = makeGetTask();
    const getFilteredTask = makeGetFilteredTask();

    return ({ projects, tasks, notifications, milestones, taskGroups }, ownProps) => {
        const task = ownProps.id && getTask(tasks.items, ownProps.id);
        const filteredTask = task && getFilteredTask(task, tasks.filter);

        return {
            removed: task?.removed,
            order: task?.order,
            completed: task?.completed || ownProps.completed,
            archived: task?.archived || ownProps.archived,
            taskGroupId: ownProps.taskGroupId || task?.task_group,
            milestoneId: ownProps.milestoneId || task?.milestone,
            isTemp: isTempId(ownProps.id),
            filtered: !filteredTask && task
        }
    }
}

export default connect(makeMapStateToProps, null, null, { forwardRef: true })(React.memo(TaskDNDDraggable));
